import {
  Control,
  Controller,
  FieldErrors,
  FieldPath,
  FieldValues,
} from "react-hook-form";

import { CodeVerification } from "~/common/components";
import SignUpFormLayout from "~/common/components/signUp/SignUpFormLayout";
import SignUpProgress from "~/common/components/signUp/SignUpProgress";
import { useCodeVerificationCooldown } from "~/common/hooks/useCodeVerificationCooldown";
import { MobileNumberVerificationSchemaType } from "~/common/utils/signUpSchemas";
import { useOnSignUpTeamMemberStore } from "~/providers/stores/useOnSignUpTeamMemberStore";

interface MobileNumberVerificationPageProps<T extends FieldValues> {
  onNext: () => Promise<void>;
  onRetry?: () => void;
  canResendAt: number;
  handleGoBack: () => void;
  step: number;
  totalSteps: number;
  control?: Control<T>;
  codeFieldKey: FieldPath<T>;
  pendingRequest?: boolean;
  errors?: FieldErrors<MobileNumberVerificationSchemaType>;
}

export const MobileNumberVerificationPage = <T extends FieldValues>({
  onNext,
  step,
  handleGoBack,
  totalSteps,
  control,
  errors,
  codeFieldKey,
  onRetry,
  canResendAt,
  pendingRequest,
}: MobileNumberVerificationPageProps<T>) => {
  const { cooldown } = useCodeVerificationCooldown(canResendAt);
  const { newTeamMember } = useOnSignUpTeamMemberStore();
  return (
    <>
      <SignUpProgress step={step} totalSteps={totalSteps} />
      <SignUpFormLayout
        title="Verification"
        description={`Enter the code sent by SMS to ${newTeamMember?.phoneNumber}`}
        handleData={onNext}
        handleGoBack={handleGoBack}
        pendingRequest={pendingRequest}
        disableContinue={pendingRequest}
      >
        <Controller
          name={codeFieldKey}
          control={control}
          render={({ field }) => {
            return (
              <CodeVerification
                value={field.value}
                onChange={field.onChange}
                onRetry={onRetry}
                error={errors?.code?.message}
                inCooldownTime={cooldown.active}
                remainingTime={cooldown.remainingTime}
                disabled={pendingRequest}
              />
            );
          }}
        />
      </SignUpFormLayout>
    </>
  );
};
